import type { BoardData } from '../model/schema.js';
import { BoardData as BoardDataSchema } from '../model/schema.js';
import { migrate } from '../doc/migrate.js';

/** Format tag of the documented JSON export (decision R12). */
export const BOARD_EXPORT_FORMAT = 'stageplottter.board.v1';

export interface BoardExport {
  format: typeof BOARD_EXPORT_FORMAT;
  exportedAt: string;
  data: BoardData;
}

export type ImportResult =
  | { ok: true; data: BoardData }
  | { ok: false; error: 'invalid-json' | 'wrong-format' | 'invalid-data'; message: string };

/** Wraps a board in the export envelope (validated). */
export function exportBoardJson(data: BoardData, now: Date = new Date()): BoardExport {
  return {
    format: BOARD_EXPORT_FORMAT,
    exportedAt: now.toISOString(),
    data: BoardDataSchema.parse(data),
  };
}

/**
 * Reads an export (JSON text or parsed object): checks the envelope, migrates old schema versions
 * and validates the data. Never throws.
 */
export function importBoardJson(json: string | unknown): ImportResult {
  let parsed: unknown = json;
  if (typeof json === 'string') {
    try {
      parsed = JSON.parse(json);
    } catch (e) {
      return { ok: false, error: 'invalid-json', message: (e as Error).message };
    }
  }
  const envelope = parsed as Partial<BoardExport> | null;
  if (
    typeof envelope !== 'object' ||
    envelope === null ||
    envelope.format !== BOARD_EXPORT_FORMAT
  ) {
    return { ok: false, error: 'wrong-format', message: `Expected format ${BOARD_EXPORT_FORMAT}` };
  }
  try {
    return { ok: true, data: migrate(envelope.data) };
  } catch (e) {
    return { ok: false, error: 'invalid-data', message: (e as Error).message };
  }
}
